import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';

const TeamJobs = () => {
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [appliedUsers, setAppliedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    jobType: 'all',
    search: ''
  });
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    avgApplicationsPerJob: 0
  });

  useEffect(() => {
    // Check if company filter was passed from navigation
    if (location.state?.companyFilter) {
      setCompanyFilter(location.state.companyFilter);
      setCompanyName(location.state.companyName || '');
      console.log('Company filter applied:', location.state.companyFilter, location.state.companyName);
    }
    fetchJobs();
  }, [location.state]);

  useEffect(() => {
    filterJobs();
  }, [jobs, filters, companyFilter]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const jobsSnapshot = await getDocs(collection(db, 'jobs'));
      const jobsData = [];

      for (const jobDoc of jobsSnapshot.docs) {
        const jobData = { id: jobDoc.id, ...jobDoc.data() };

        // Fetch company details
        if (jobData.companyId) {
          try {
            const companyDoc = await getDoc(doc(db, 'companies', jobData.companyId));
            if (companyDoc.exists()) {
              jobData.companyDetails = companyDoc.data();
            }
          } catch (error) {
            console.error('Error fetching company details:', error);
          }
        }

        // Get application count
        try {
          const applicationsSnapshot = await getDocs(collection(db, 'applications'));
          const applicationCount = applicationsSnapshot.docs.filter(
            appDoc => appDoc.data().jobId === jobData.id
          ).length;
          jobData.applicationCount = applicationCount;
        } catch (error) {
          console.error('Error fetching applications:', error);
          jobData.applicationCount = 0;
        }

        jobsData.push(jobData);
      }

      setJobs(jobsData);

      // Calculate stats
      const totalJobs = jobsData.length;
      const activeJobs = jobsData.filter(job => job.status === 'active').length;
      const totalApplications = jobsData.reduce((sum, job) => sum + (job.applicationCount || 0), 0);
      const avgApplicationsPerJob = totalJobs > 0 ? (totalApplications / totalJobs).toFixed(1) : 0;

      setStats({
        totalJobs,
        activeJobs,
        totalApplications,
        avgApplicationsPerJob
      });

    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = [...jobs];

    // Filter by company if set
    if (companyFilter) {
      filtered = filtered.filter(job => job.userId === companyFilter || job.companyId === companyFilter);
      console.log('Filtered by company:', companyFilter, 'Results:', filtered.length);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(job => job.status === filters.status);
    }

    if (filters.jobType !== 'all') {
      filtered = filtered.filter(job => job.jobType === filters.jobType);
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(job => {
        const locationText = typeof job.location === 'object'
          ? `${job.location.city || ''} ${job.location.state || ''} ${job.location.country || ''}`.toLowerCase()
          : (job.location || '').toLowerCase();

        return job.title?.toLowerCase().includes(searchTerm) ||
          job.companyDetails?.name?.toLowerCase().includes(searchTerm) ||
          locationText.includes(searchTerm);
      });
    }

    // Sort by creation date (latest first)
    filtered.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB - dateA; // Latest first
    });

    setFilteredJobs(filtered);
  };

  const fetchAppliedUsers = async (jobId) => {
    setLoadingUsers(true);
    console.log('Fetching applied users for job:', jobId);
    try {
      const applicationsSnapshot = await getDocs(collection(db, 'applications'));
      console.log('Total applications in DB:', applicationsSnapshot.docs.length);

      const jobApplications = applicationsSnapshot.docs.filter(
        doc => doc.data().jobId === jobId
      );
      console.log('Applications for this job:', jobApplications.length);

      // Map applications to user data - using embedded data from application document
      const usersData = jobApplications.map(appDoc => {
        const appData = appDoc.data();
        console.log('Processing application:', appDoc.id, appData);

        // Use the embedded applicant data from the application document
        // This matches the structure from applicationService.js (lines 73-104)
        return {
          id: appDoc.id,
          applicationId: appDoc.id,
          userId: appData.applicantId || appData.userId,
          appliedAt: appData.appliedAt,
          status: appData.status || 'pending',
          coverLetter: appData.coverLetter || '',
          resume: appData.resumeURL || appData.resume || '',
          // Applicant details (embedded in application)
          name: appData.applicantName || 'Unknown',
          email: appData.applicantEmail || 'No email',
          phone: appData.applicantPhone || 'No phone',
          experience: formatExperience(appData.applicantProfile?.experience || appData.experience),
          skills: appData.applicantProfile?.skills || appData.skills || [],
          location: appData.applicantLocation || appData.location || 'Not specified',
          profilePicture: appData.applicantProfile?.profilePicture || null,
          // Additional profile data
          professionalSummary: appData.applicantProfile?.professionalSummary || '',
          education: appData.applicantProfile?.education || [],
          expectedSalary: appData.applicantProfile?.expectedSalary || null,
          noticePeriod: appData.applicantProfile?.noticePeriod || ''
        };
      });

      // Sort by application date (latest first)
      usersData.sort((a, b) => {
        const dateA = a.appliedAt?.toDate ? a.appliedAt.toDate() : new Date(a.appliedAt);
        const dateB = b.appliedAt?.toDate ? b.appliedAt.toDate() : new Date(b.appliedAt);
        return dateB - dateA;
      });

      console.log('Final users data:', usersData);
      setAppliedUsers(usersData);
    } catch (error) {
      console.error('Error fetching applied users:', error);
      setAppliedUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowJobModal(true);
    fetchAppliedUsers(job.id);
  };

  const exportAppliedUsers = () => {
    if (appliedUsers.length === 0) return;

    const csvContent = [
      ['Name', 'Email', 'Phone', 'Experience', 'Skills', 'Location', 'Applied Date', 'Status', 'Cover Letter'],
      ...appliedUsers.map(user => [
        user.name,
        user.email,
        user.phone,
        user.experience, // Already formatted by formatExperience function
        Array.isArray(user.skills) ? user.skills.join(', ') : user.skills,
        user.location,
        formatDate(user.appliedAt),
        user.status,
        user.coverLetter || 'No cover letter'
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedJob?.title || 'job'}_applications.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString();
  };

  const formatSalary = (salaryObj) => {
    if (!salaryObj) return 'Not specified';

    // Handle new salary object structure
    if (typeof salaryObj === 'object' && salaryObj.min && salaryObj.max) {
      const min = parseInt(salaryObj.min);
      const max = parseInt(salaryObj.max);
      const currency = salaryObj.currency === 'USD' ? '$' : '₹';
      const period = salaryObj.period || 'month';
      const negotiable = salaryObj.negotiable ? ' (Negotiable)' : '';

      return `${currency}${min.toLocaleString()} - ${currency}${max.toLocaleString()}/${period}${negotiable}`;
    }

    // Handle legacy format
    if (typeof salaryObj === 'number') return `₹${salaryObj.toLocaleString()}`;

    return 'Not specified';
  };

  const formatExperience = (experience) => {
    if (!experience) return 'Not specified';

    // If it's already a string, return it
    if (typeof experience === 'string') return experience;

    // If it's an array of work experience objects
    if (Array.isArray(experience) && experience.length > 0) {
      const totalYears = experience.reduce((total, exp) => {
        if (exp.isCurrentJob) {
          const startDate = new Date(exp.startDate?.toDate?.() || exp.startDate);
          const now = new Date();
          const years = (now - startDate) / (1000 * 60 * 60 * 24 * 365);
          return total + years;
        } else if (exp.startDate && exp.endDate) {
          const startDate = new Date(exp.startDate?.toDate?.() || exp.startDate);
          const endDate = new Date(exp.endDate?.toDate?.() || exp.endDate);
          const years = (endDate - startDate) / (1000 * 60 * 60 * 24 * 365);
          return total + years;
        }
        return total;
      }, 0);

      return `${Math.round(totalYears)} years`;
    }

    // If it's a single work experience object
    if (typeof experience === 'object' && experience.jobTitle) {
      return `${experience.jobTitle} at ${experience.company || 'Unknown Company'}`;
    }

    return 'Not specified';
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {companyFilter && companyName ? `Jobs by ${companyName}` : 'Jobs Management'}
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              {companyFilter && companyName
                ? `Viewing all jobs posted by ${companyName}`
                : 'Monitor and manage all job postings on the platform'
              }
            </p>
          </div>
          {companyFilter && (
            <button
              onClick={() => {
                setCompanyFilter(null);
                setCompanyName('');
                window.history.replaceState({}, document.title);
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium flex items-center gap-2 self-start sm:self-auto"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear Company Filter
            </button>
          )}
        </div>
        {companyFilter && companyName && (
          <div className="mt-3 inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Filtered by Company: {companyName}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center">
            <div className="p-3 bg-blue-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalJobs}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
          <div className="flex items-center">
            <div className="p-3 bg-green-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeJobs}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
          <div className="flex items-center">
            <div className="p-3 bg-purple-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Applications</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200">
          <div className="flex items-center">
            <div className="p-3 bg-orange-500 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Applications</p>
              <p className="text-2xl font-bold text-gray-900">{stats.avgApplicationsPerJob}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Jobs</label>
            <input
              type="text"
              placeholder="Search by title, company, location..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Job Type</label>
            <select
              value={filters.jobType}
              onChange={(e) => setFilters(prev => ({ ...prev, jobType: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="full-time">Full Time</option>
              <option value="part-time">Part Time</option>
              <option value="contract">Contract</option>
              <option value="internship">Internship</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ status: 'all', jobType: 'all', search: '' })}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            All Jobs ({filteredJobs.length})
          </h2>
          <div className="text-sm text-gray-500">
            Sorted by latest first
          </div>
        </div>

        {filteredJobs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {filteredJobs.map((job) => (
              <div
                key={job.id}
                onClick={() => handleJobClick(job)}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 hover:shadow-xl cursor-pointer transition-all duration-300 hover:border-blue-300 transform hover:-translate-y-1"
              >
                {/* Job Header */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors">
                        {job.title}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${job.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : job.status === 'closed'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                        {job.status?.toUpperCase()}
                      </span>
                    </div>

                    {/* Company and Date */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-gray-600 mb-3">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="font-medium">{job.companyDetails?.name || job.company || 'Unknown Company'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                        </svg>
                        <span className="text-sm">Posted: {formatDate(job.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Applications Count */}
                  <div className="self-start sm:self-auto">
                    <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                      <div className="flex items-center gap-2 text-blue-700">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="font-bold text-lg">{job.applicationCount || 0}</span>
                      </div>
                      <p className="text-xs text-blue-600 font-medium">Applications</p>
                    </div>
                  </div>
                </div>

                {/* Job Details Grid */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">Location</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">
                        {job.location?.city || job.location?.state || job.location?.country
                          ? `${job.location.city || ''}, ${job.location.state || ''}, ${job.location.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                          : typeof job.location === 'string' ? job.location : 'Not specified'
                        }
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">Salary</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">{formatSalary(job.salary)}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">Type</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-900 capitalize break-words">{job.jobType?.replace('-', ' ') || 'Not specified'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">Experience</p>
                      <p className="text-xs sm:text-sm font-medium text-gray-900 capitalize break-words">{job.experienceLevel || 'Not specified'}</p>
                    </div>
                  </div>
                </div>

                {/* Job Tags */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {job.urgent && (
                    <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                      🔥 URGENT
                    </span>
                  )}
                  {job.featured && (
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                      ⭐ FEATURED
                    </span>
                  )}
                  {job.department && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                      {job.department.toUpperCase()}
                    </span>
                  )}
                  {job.location?.hybrid && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                      🏢 HYBRID
                    </span>
                  )}
                  {job.location?.remote && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                      🏠 REMOTE
                    </span>
                  )}
                </div>

                {/* Job Description */}
                <p className="text-gray-600 text-xs sm:text-sm mb-4 leading-relaxed break-words">
                  {job.description?.substring(0, 200)}...
                </p>

                {/* Job Footer */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-4 border-t border-gray-100">
                  <div className="text-xs sm:text-sm text-gray-500">
                    Deadline: {formatDate(job.applicationDeadline) || 'Not specified'}
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-xs sm:text-sm text-blue-600 font-medium">View Details</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="text-gray-400 text-6xl mb-4">💼</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
            <p className="text-gray-500">No jobs match your current filters.</p>
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      {showJobModal && selectedJob && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-2xl sm:rounded-3xl max-w-6xl w-full max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100">
            {/* Header with gradient background */}
            <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-4 sm:p-6 lg:p-8">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4 sm:gap-6">
                  {/* Job Icon */}
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-2xl sm:text-3xl border-4 border-white/20 shadow-lg">
                    💼
                  </div>

                  <div className="text-white">
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2">{selectedJob.title}</h2>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3">
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm border ${selectedJob.status === 'active'
                          ? 'bg-green-500/20 text-green-100 border-green-400/30'
                          : selectedJob.status === 'closed'
                            ? 'bg-red-500/20 text-red-100 border-red-400/30'
                            : 'bg-yellow-500/20 text-yellow-100 border-yellow-400/30'
                        }`}>
                        {selectedJob.status === 'active' ? '✓ ACTIVE JOB' : selectedJob.status === 'closed' ? '✗ CLOSED' : '⏳ PENDING'}
                      </span>
                      <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20">
                        {selectedJob.companyDetails?.name || selectedJob.company || 'Unknown Company'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-white/80 text-sm sm:text-base">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm">
                          {selectedJob.location?.city || selectedJob.location?.state || selectedJob.location?.country
                            ? `${selectedJob.location.city || ''}, ${selectedJob.location.state || ''}, ${selectedJob.location.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
                            : typeof selectedJob.location === 'string' ? selectedJob.location : 'Location not specified'
                          }
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                        <span className="text-sm font-semibold">{formatSalary(selectedJob.salary)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="text-sm font-semibold">{selectedJob.applicationCount || 0} Applications</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setShowJobModal(false)}
                  className="absolute top-2 right-2 sm:relative sm:top-auto sm:right-auto p-2 sm:p-3 hover:bg-white/10 rounded-xl transition-colors text-white/80 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="p-8 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 200px)' }}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Job Details */}
                <div className="space-y-6">
                  {/* Job Description */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Job Description</h3>
                    </div>
                    <div className="prose max-w-none text-gray-700 leading-relaxed">
                      {typeof selectedJob.description === 'string'
                        ? selectedJob.description.split('\n').map((paragraph, index) => (
                          <p key={index} className="mb-4 text-gray-600">{paragraph}</p>
                        ))
                        : <p className="mb-4 text-gray-600">{selectedJob.description || 'No description available'}</p>
                      }
                    </div>
                  </div>

                  {/* Requirements */}
                  {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Requirements</h3>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        {Array.isArray(selectedJob.requirements)
                          ? selectedJob.requirements.filter(req => req && req.trim()).map((requirement, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-gray-700">{requirement}</span>
                            </div>
                          ))
                          : typeof selectedJob.requirements === 'string'
                            ? selectedJob.requirements.split('\n').map((requirement, index) => (
                              <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-gray-700">{requirement}</span>
                              </div>
                            ))
                            : <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-gray-700">{selectedJob.requirements}</span>
                            </div>
                        }
                      </div>
                    </div>
                  )}

                  {/* Benefits */}
                  {selectedJob.benefits && selectedJob.benefits.length > 0 && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Benefits & Perks</h3>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        {Array.isArray(selectedJob.benefits)
                          ? selectedJob.benefits.filter(benefit => benefit && benefit.trim()).map((benefit, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                              <span className="text-gray-700">{benefit}</span>
                            </div>
                          ))
                          : typeof selectedJob.benefits === 'string'
                            ? selectedJob.benefits.split('\n').map((benefit, index) => (
                              <div key={index} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                <span className="text-gray-700">{benefit}</span>
                              </div>
                            ))
                            : <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                              <span className="text-gray-700">{selectedJob.benefits}</span>
                            </div>
                        }
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - Applications */}
                <div className="space-y-6">
                  {/* Applications Section */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Applications ({appliedUsers.length})</h3>
                      </div>
                      {appliedUsers.length > 0 && (
                        <button
                          onClick={exportAppliedUsers}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          Export CSV
                        </button>
                      )}
                    </div>

                    {loadingUsers ? (
                      <div className="text-center py-8">
                        <LoadingSpinner />
                        <p className="text-gray-500 mt-2">Loading applications...</p>
                      </div>
                    ) : appliedUsers.length > 0 ? (
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {appliedUsers.map((user) => (
                          <div key={user.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                  </div>
                                  <div>
                                    <h4 className="font-semibold text-gray-900">{user.name}</h4>
                                    <p className="text-sm text-gray-600">{user.email}</p>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3 text-sm">
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    <span>{user.phone}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span>{user.location}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    <span>{user.experience}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h8m-8 0H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2m-8 0V7" />
                                    </svg>
                                    <span>Applied: {formatDate(user.appliedAt)}</span>
                                  </div>
                                </div>

                                {user.coverLetter && (
                                  <div className="mb-3">
                                    <h5 className="text-sm font-semibold text-gray-700 mb-1">Cover Letter:</h5>
                                    <p className="text-sm text-gray-600 bg-white p-3 rounded-lg border">{user.coverLetter}</p>
                                  </div>
                                )}

                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${user.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                      user.status === 'reviewed' ? 'bg-blue-100 text-blue-700' :
                                        user.status === 'shortlisted' ? 'bg-purple-100 text-purple-700' :
                                          user.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                            'bg-green-100 text-green-700'
                                    }`}>
                                    {user.status?.charAt(0).toUpperCase() + user.status?.slice(1) || 'Pending'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-400 text-4xl mb-3">📄</div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No applications yet</h4>
                        <p className="text-gray-500">No one has applied for this job yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamJobs;
